/**
 * UserReasonDialog — dialogue générique avec motif obligatoire.
 *
 * Utilisé pour :
 *   - Suspendre un utilisateur  → POST /users/{id}/suspend
 *   - Supprimer un utilisateur  → DELETE /users/{id}
 *
 * Le champ « Motif » est obligatoire (min 3 caractères, trim).
 */
import { useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useRequiredFields, requiredClass } from "@/lib/useRequiredFields";
import { FieldError } from "@/components/common/RequiredField";

export default function UserReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  submitVariant = "danger",   // "danger" (rouge) | "warning" (orange)
  onSubmit,                     // async (reason) => Promise
  testidPrefix = "user-reason",
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setReason(""); setBusy(false); }
  }, [open]);

  const trimmed = reason.trim();
  const invalid = trimmed.length < 3;
  const req = useRequiredFields([{ key: "reason", label: "Motif", ok: !invalid }]);

  const confirm = async () => {
    if (!req.validate()) return;
    setBusy(true);
    try {
      await onSubmit(trimmed);
      onOpenChange(false);
    } catch (err) {
      toast.error("Action refusée", { description: err?.response?.data?.detail || err?.message || "" });
    } finally {
      setBusy(false);
    }
  };

  const submitClass = submitVariant === "warning"
    ? "bg-status-pending hover:bg-status-pending/90 text-black"
    : "bg-status-danger hover:bg-status-danger/90 text-white";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent
        className="max-w-md bg-card border-vertec-borderDark"
        data-testid={`${testidPrefix}-dialog`}
      >
        <DialogHeader>
          <DialogTitle className="text-lg">{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-muted-foreground text-sm">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-2 py-2" data-field="reason">
          <Label htmlFor="reason" className="text-sm text-muted-foreground">
            Motif <span className="text-status-danger">*</span>
          </Label>
          <Textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Expliquez la raison de cette action…"
            rows={4}
            className={`resize-none ${requiredClass(req.has("reason"))}`}
            disabled={busy}
            data-testid={`${testidPrefix}-textarea`}
            data-no-capitalize
          />
          <FieldError show={req.has("reason")} message="Motif obligatoire (3 caractères minimum)" testId={`${testidPrefix}-reason-error`} />
          <p className="text-[11px] text-muted-foreground">
            Ce motif sera enregistré dans l&apos;historique et visible par les administrateurs.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid={`${testidPrefix}-cancel`}
          >
            Annuler
          </Button>
          <Button
            className={submitClass}
            onClick={confirm}
            disabled={busy}
            data-incomplete={invalid || undefined}
            data-testid={`${testidPrefix}-confirm`}
          >
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
